import type { PerspectiveCamera, SceneControl } from '@anov/3d-core'
import { CanvasTexture, DoubleSide, Mesh, NearestFilter, Object3D, PlaneGeometry, ShaderMaterial, Vector2, use } from '@anov/3d-core'
import h from 'anov-heat-map'
import type { AnyProps } from 'supercluster'
import Supercluster from 'supercluster'
import { heightFragmentShader, heightVertexShader } from './shader'
import type { HeatMapData, HeatMapnterface } from './index'

type RangeType = {
  min: number
  max: number
}

type HeatMapOptions = {
  colorMap?: {
    [key: number]: string
  }
  max?: number
  radius?: number
  height?: number
  isPixel?: boolean
  range?: RangeType
}

const defaultGradient = { 0.25: 'rgb(0,0,255)', 0.55: 'rgb(0,255,0)', 0.85: 'yellow', 1.0: 'rgb(255,0,0)' }

class HeatMap extends Object3D implements HeatMapnterface {
  public data: HeatMapData[] = []

  private _height: number
  private _radius: number
  private _heatmap: any
  private _url: string
  private _colorMap: {
    [key: number]: string
  }

  private _isPixel: boolean
  private _canvasBox: HTMLDivElement | undefined
  private _attachmentMesh: Mesh | undefined
  private _supercluster: Supercluster
  private _range: RangeType | undefined
  private sceneControl?: SceneControl

  private get defaultMax() {
    return Math.max(...this.data.map(item => item.value))
  }

  public get url() {
    return this._url
  }

  constructor(options: HeatMapOptions, sceneControl?: SceneControl) {
    super()
    this.sceneControl = sceneControl

    this._colorMap = options.colorMap || defaultGradient
    this._radius = options.radius ?? 40
    this._height = options.height ?? 70
    this._isPixel = options.isPixel ?? false
    this._range = options.range
  }

  private initSupercluster() {
    this._supercluster = new Supercluster({
      radius: 40,
      minZoom: 1,
      maxZoom: 16,
    })
  }

  private setSuperclusterData() {
    const featuresPoints = this.data.map((item) => {
      return {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [item.x, item.z],
        },
      }
    })

    console.log('正在加载点数据到supercluster:', featuresPoints.length)
    this._supercluster.load(featuresPoints as Supercluster.PointFeature<AnyProps>[])
  }

  private getZoomLevel(camera: PerspectiveCamera) {
    const minZoom = 10
    const maxZoom = 16
    const minHeight = 10
    const maxHeight = 1000

    return Math.max(minZoom, Math.min(maxZoom,
      ((maxHeight - camera.position.y) / (maxHeight - minHeight)) * (maxZoom - minZoom) + minZoom,
    ))
  }

  private getSuperclusterData() {
    const { max } = this.calDataMaxMin()
    const zoom = this.getZoomLevel(use.useScene(this.sceneControl).camera)
    const clusters = this._supercluster.getClusters([-max, -max, max, max], zoom)

    console.log('当前缩放级别:', zoom, '输入点数量:', this.data.length, '聚类后点数量:', clusters.length)

    return clusters
  }

  private calDataMaxMin() {
    const maxX = Math.max(...this.data.map(item => item.x))
    const minX = Math.min(...this.data.map(item => item.x))
    const maxZ = Math.max(...this.data.map(item => item.z))
    const minZ = Math.min(...this.data.map(item => item.z))

    const max = Math.max(Math.abs(Math.ceil(maxX)), Math.abs(Math.ceil(minX)), Math.abs(Math.ceil(maxZ)), Math.abs(Math.ceil(minZ)), 60)
    const maxRadius = Math.max(...this.data.map(item => item.radius ?? this._radius), this._radius)

    const width = max * 2
    const height = max * 2

    const realShowWidth = maxX - minX
    const realShowHeight = maxZ - minZ

    const realShowMax = Math.max(Math.ceil(realShowWidth), Math.ceil(realShowHeight), 60)

    return { width, height, max, minX, minZ, maxX, maxZ, realShowMax, realShowWidth, realShowHeight, maxRadius }
  }

  private generateAsitePoints(data: HeatMapData[]) {
    const maxX = Math.max(...data.map(item => item.x))
    const minX = Math.min(...data.map(item => item.x))
    const maxZ = Math.max(...data.map(item => item.z))
    const minZ = Math.min(...data.map(item => item.z))

    const maxRadius = 2 * Math.max(...data.map(item => item.radius ?? this._radius), this._radius)

    const boundaryPoints = [
      { x: minX - maxRadius, z: minZ - maxRadius, value: 0, y: 0, isBoundary: true, radius: 0.001 },
      { x: maxX + maxRadius, z: minZ - maxRadius, value: 0, y: 0, isBoundary: true, radius: 0.001 },
      { x: minX - maxRadius, z: maxZ + maxRadius, value: 0, y: 0, isBoundary: true, radius: 0.001 },
      { x: maxX + maxRadius, z: maxZ + maxRadius, value: 0, y: 0, isBoundary: true, radius: 0.001 },
    ]

    return { boundaryPoints, maxRadius }
  }

  private get newOrigin() {
    const { minX, minZ, maxX, maxZ } = this.calDataMaxMin()

    return {
      x: (maxX + minX) / 2,
      z: (maxZ + minZ) / 2,
    }
  }

  private coordinate3DTransformation(data: HeatMapData[]) {
    const { x, z } = this.newOrigin
    return data.map(item => ({
      ...item,
      x: item.x - x,
      z: item.z - z,
      radius: item.radius ?? this._radius,
    }))
  }

  private coordinateTransformation(data: HeatMapData[]) {
    const hanldeData = this.coordinate3DTransformation(data)
    const { width, height, realShowWidth, realShowHeight } = this.calDataMaxMin()
    const resolutionScale = this.calResolutionScale()

    const result = hanldeData.map(item => ({
      x: Math.floor((item.x - (-realShowWidth / 2)) / (realShowWidth / 2 - (-realShowWidth / 2)) * width) * resolutionScale,
      y: Math.floor((item.z - (-realShowHeight / 2)) / (realShowHeight / 2 - (-realShowHeight / 2)) * height) * resolutionScale,
      value: item.value,
      radius: (item.radius ?? this._radius) * resolutionScale * 2,
      // @ts-ignore
      isBoundary: item?.isBoundary,
    }))

    return result
  }

  private createHeatmapTool() {
    const heatmap = h.create({
      container: this._canvasBox,
      radius: this._radius,
      gradient: this._colorMap,
      maxOpacity: 1,
      minOpacity: 0,
    })

    return heatmap
  }

  private createCanvas(width: number, height: number) {
    const canvasBox = document.createElement('div')

    canvasBox.style.width = `${width}px`
    canvasBox.style.height = `${height}px`
    canvasBox.style.top = '0'
    canvasBox.style.right = '0'

    use.useScene(this.sceneControl).domElement.parentElement.appendChild(canvasBox)

    return canvasBox
  }

  private calResolutionScale() {
    const hanldeData = this.coordinate3DTransformation(this.data)
    const { width, height, realShowWidth, realShowHeight } = this.calDataMaxMin()
    const result = hanldeData.map(item => ({
      x: Math.floor((item.x - (-realShowWidth / 2)) / (realShowWidth / 2 - (-realShowWidth / 2)) * width),
      y: Math.floor((item.z - (-realShowHeight / 2)) / (realShowHeight / 2 - (-realShowHeight / 2)) * height),
      value: item.value,
      radius: item.radius * 2,
    }))

    const maxRadius = Math.max(...result.map(item => item.radius))
    const maxX = Math.max(...result.map(item => item.x))
    const maxY = Math.max(...result.map(item => item.y))
    const max = Math.max(maxX, maxY)

    if (max <= 1000) {
      const scale = (1000 - max) / 1000 * 15 + 1
      return Math.floor(maxRadius < 40 ? scale : 1)
    }

    if (max <= 5000) {
      const scale = (5000 - max) / 5000 * 5 + 1
      return Math.floor(maxRadius < 40 ? scale : 1)
    }

    return 1
  }

  private createAttachmentMesh() {
    if (this._attachmentMesh)
      this.remove(this._attachmentMesh)

    const { realShowMax, max, realShowWidth, realShowHeight } = this.calDataMaxMin()
    const canvas = this._heatmap._renderer.canvas
    const texture = new CanvasTexture(canvas)

    texture.minFilter = NearestFilter
    texture.magFilter = NearestFilter

    this._url = canvas.toDataURL('image/png')

    const threshold = (0.3 / 1000 * realShowMax)
    const shaderMaterial = new ShaderMaterial({
      uniforms: {
        map: { value: texture },
        granularity: { value: realShowMax / 3 < 300 ? 300 : realShowMax / 3 },
        threshold: { value: threshold > 0.6 ? 0.6 : threshold < 0.25 ? 0.25 : threshold },
        uOpacity: { value: 2.0 },
        uHeight: { value: this._height },
        isPixel: { value: this._isPixel },
        uRange: { value: new Vector2(-max, max) },

      },
      vertexShader: heightVertexShader,
      fragmentShader: heightFragmentShader,
      transparent: true,
      side: DoubleSide,
      depthTest: false,
    })

    const mesh = new Mesh(new PlaneGeometry(realShowWidth, realShowHeight, 500, 500), shaderMaterial)
    const { x, z } = this.newOrigin

    mesh.rotation.x = -Math.PI / 2
    mesh.position.set(x, 0, z)
    mesh.renderOrder = 1000

    this._attachmentMesh = mesh
    this.add(mesh)
  }

  public setData(data: HeatMapData[]) {
    const { boundaryPoints } = this.generateAsitePoints(data)
    this.data = [...boundaryPoints, ...data]

    const heatmapData = this.coordinateTransformation(this.data)
    this._canvasBox && use.useScene(this.sceneControl).domElement.parentElement.removeChild(this._canvasBox)
    this._canvasBox = this.createCanvas(Math.max(...heatmapData.map(item => item.x)), Math.max(...heatmapData.map(item => item.y)))
    this._heatmap = this.createHeatmapTool()

    this._heatmap.setData({
      data: heatmapData.filter(item => !item.isBoundary),
      max: this._range?.max ?? this.defaultMax,
      min: this._range?.min ?? 0,
    })

    this.createAttachmentMesh()
  }

  override removeFromParent(): this {
    super.removeFromParent()
    if (this.parent) {
      use.useScene(this.sceneControl).domElement.parentElement.removeChild(this._canvasBox)

      // @ts-ignore
      this._attachmentMesh?.material.dispose()
    }

    return this
  }
}

export default HeatMap
